import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { ChangeEvent, FC, useState } from 'react';

import {
  areaColorOf, CellColorArea, DispatchCellColorSettings, prefectureColorOf,
} from './resolveCellColor';

/** 会社が変えた色だけを持つ部分。API へ送る形そのまま */
type ColorDraft = Pick<DispatchCellColorSettings, 'area_colors' | 'prefecture_colors'>;

/**
 * 色が引けない区分に当てる値。`<input type="color">` は `#rrggbb` 以外を受け取れず、
 * 空文字を渡すとブラウザが黙って `#000000` に直したうえで React と値がずれる。
 */
const FALLBACK_COLOR = '#000000';

const sameColors = (a: Record<string, string>, b: Record<string, string>): boolean => {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((key) => a[key] === b[key]);
};

/**
 * 区分 1 件の行（サンプル route-planner `CellColorSettingsDialog.tsx:22-60`）。
 * 左から 色見本・区分名・補足（エリアタブの所属県）・右端に HEX。
 */
const ColorRow: FC<{
  name: string;
  sub?: string;
  value: string;
  onChange: (color: string) => void;
}> = ({ name, sub, value, onChange }) => (
  <Box
    data-testid="cell-color-row"
    data-name={name}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      px: 1,
      py: 0.75,
      borderBottom: '1px solid hsl(var(--border))',
    }}
  >
    <Box
      component="input"
      type="color"
      value={value}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      aria-label={`${name} の色`}
      sx={{
        width: 28,
        height: 22,
        p: 0,
        border: '1px solid hsl(var(--border))',
        borderRadius: '4px',
        background: 'transparent',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    />
    <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 96 }}>{name}</Typography>
    {sub && (
      <Typography
        sx={{
          fontSize: 11,
          color: 'text.secondary',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {sub}
      </Typography>
    )}
    <Box sx={{ ml: 'auto', fontSize: 11, color: 'text.secondary' }}>{value}</Box>
  </Box>
);

type Props = {
  /**
   * 呼び出し側は開いているあいだだけこれを描く。編集中の色（`draft`）は開くたびに
   * 保存済みの色から始める必要があり、閉じても生き残る形にすると前回の編集が残る。
   */
  open: boolean;
  settings: DispatchCellColorSettings;
  /** API が返すエリアと所属県。エリアタブの所属県と、都道府県タブの束ね方に使う */
  areas: readonly CellColorArea[];
  onClose: () => void;
  /** 設定全体を渡す。API の PUT は置き換えなので、部分更新は作らない */
  onSave: (next: DispatchCellColorSettings) => void;
};

/**
 * セル色マスタ設定（log_web_issues#338 C2）。サンプル route-planner
 * `src/components/dispatch/CellColorSettingsDialog.tsx` に合わせて、全区分の色を
 * 1 画面で見渡せる形にする。
 *
 * Why not サンプルの「方面」タブも出す: 上り・下りの判定は首都圏を基準にした決め打ちで
 * （route-planner regionMaster.ts:52-60）、会社ごとの拠点では合わない。出さないことを
 * ユーザーと決めた（2026-09-25）。
 *
 * Why not 色を変えるたびに保存する（サンプルは localStorage へ即時保存）: こちらの保存先は
 * API で、PUT は設定全体の置き換え。`<input type="color">` はスライダーを動かすあいだ
 * change が連続で出るので、1 変更 1 PUT にすると 57 区分ぶんどころか 1 区分でも連打になり、
 * 途中の色がそのつど全社の設定として保存される。閉じるときに 1 回だけ送る。
 *
 * Why not 「保存」と「キャンセル」の 2 ボタンにする: サンプルは閉じるだけで、取り消しの
 * 操作を持たない。押し分けを増やすより、閉じ方（ボタン・背景・Esc）のどれでも同じように
 * 残るほうが迷わない。
 */
export const CellColorDialog: FC<Props> = ({ open, settings, areas, onClose, onSave }) => {
  const [tab, setTab] = useState(0);
  const [draft, setDraft] = useState<ColorDraft>({
    area_colors: settings.area_colors,
    prefecture_colors: settings.prefecture_colors,
  });

  /** 色見本に出す値は編集中の色から引く（保存済みの色ではなく） */
  const editing: DispatchCellColorSettings = { ...settings, ...draft };
  const areaValue = (area: string) => areaColorOf(editing, area) ?? FALLBACK_COLOR;
  const prefectureValue = (prefecture: string) => (
    prefectureColorOf(editing, areas, prefecture) ?? FALLBACK_COLOR
  );

  const setAreaColor = (area: string, color: string) => setDraft((prev) => ({
    ...prev, area_colors: { ...prev.area_colors, [area]: color },
  }));
  const setPrefectureColor = (prefecture: string, color: string) => setDraft((prev) => ({
    ...prev, prefecture_colors: { ...prev.prefecture_colors, [prefecture]: color },
  }));

  /**
   * 既定へ戻すのはキーごと落とす。既定色を値として書き戻すと、既定パレットを
   * 変えたときにその区分だけ古い色のまま取り残される。
   */
  const resetColors = () => setDraft({ area_colors: {}, prefecture_colors: {} });

  const handleClose = () => {
    const changed = !sameColors(draft.area_colors, settings.area_colors)
      || !sameColors(draft.prefecture_colors, settings.prefecture_colors);
    if (changed) onSave(editing);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700 }}>セル色マスタ設定</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Tabs
          value={tab}
          onChange={(_, next: number) => setTab(next)}
          sx={{ borderBottom: '1px solid hsl(var(--border))', minHeight: 38 }}
        >
          <Tab label="エリア（10区分）" sx={{ fontSize: 12, minHeight: 38 }} />
          <Tab label="都道府県" sx={{ fontSize: 12, minHeight: 38 }} />
        </Tabs>

        {/* 47 行あるので高さで頭打ちにして中をスクロールさせる */}
        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {tab === 0 && areas.map((area) => (
            <ColorRow
              key={area.name}
              name={area.name}
              sub={area.prefectures.join('・')}
              value={areaValue(area.name)}
              onChange={(color) => setAreaColor(area.name, color)}
            />
          ))}

          {/* 都道府県はエリアごとに束ねる。47 行を平らに並べると、いま見ているのが
              どの地方かがスクロール中に分からなくなる */}
          {tab === 1 && areas.map((area) => (
            <Box key={area.name}>
              <Typography
                data-testid="cell-color-group"
                sx={{
                  px: 1,
                  pt: 1.25,
                  pb: 0.5,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  color: 'text.secondary',
                }}
              >
                {area.name}
              </Typography>
              {area.prefectures.map((prefecture) => (
                <ColorRow
                  key={prefecture}
                  name={prefecture}
                  value={prefectureValue(prefecture)}
                  onChange={(color) => setPrefectureColor(prefecture, color)}
                />
              ))}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" sx={{ width: '100%' }} justifyContent="space-between">
          <Button size="small" color="inherit" onClick={resetColors} sx={{ fontSize: 12 }}>
            既定色に戻す
          </Button>
          <Button size="small" variant="contained" onClick={handleClose} sx={{ fontSize: 12 }}>
            閉じる
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
